import { useState } from 'react';
import { AlertTriangle, ArrowRight, CheckCircle2 } from 'lucide-react';
import type { ActualsResponse } from '../../types/strategicKpis';
import {
    checkHasFinding, dataQualityWindowText, personsWithoutTargets, targetsFindingCount, type DataQualityCheck,
} from '../../utils/strategicKpiDataQuality';
import { useBodyTableOffset } from './useTableOffset';

interface Props {
    data: ActualsResponse;
    /** «Set targets»: the page leads to the Targets tab. The page ignores the argument (it opens the tab as it is); the signature stays so a page can pass the row's person or BU later. */
    onSetTargets: (target?: { staffId?: string; buCode?: string }) => void;
}

/** A selection hides the rest of the BU, so "no targets" cannot be told - never claim it. */
const NO_TARGETS_IN_SELECTION = 'No targets in this selection - clear the filters to check the whole BU.';

const LABEL_SIZE = { fontSize: 'var(--fs-label)', lineHeight: 'var(--lh-label)' } as const;

interface CheckCardProps {
    check: DataQualityCheck;
    title: string;
    count: number;
    text: string;
    selected: boolean;
    onSelect: (c: DataQualityCheck) => void;
    testId: string;
}

function CheckCard({ check, title, count, text, selected, onSelect, testId }: CheckCardProps) {
    const found = count > 0;
    return (
        <button
            type="button"
            className="card"
            aria-pressed={selected}
            onClick={() => onSelect(check)}
            data-testid={testId}
            style={{
                display: 'grid', gridTemplateColumns: 'auto minmax(0, 1fr) auto', columnGap: 14, rowGap: 4, alignItems: 'start',
                textAlign: 'left', cursor: 'pointer', width: '100%',
                ...(selected ? { borderColor: 'var(--action)', boxShadow: 'inset 0 0 0 1px var(--action)' } : null),
            }}
        >
            <span
                aria-hidden="true"
                style={{
                    gridColumn: 1, gridRow: '1 / span 2', width: 32, height: 32, borderRadius: '50%', display: 'grid', placeItems: 'center',
                    background: found ? 'var(--attention-tint)' : 'var(--surface-sunken)', color: found ? 'var(--attention-ink)' : 'var(--text-muted)',
                }}
            >
                {found ? <AlertTriangle className="i sm" /> : <CheckCircle2 className="i sm" />}
            </span>
            <span className="strong" style={{ gridColumn: 2, gridRow: 1, fontSize: 'var(--fs-h3)', lineHeight: 'var(--lh-h3)' }}>{title}</span>
            <span className="strong num" style={{ gridColumn: 3, gridRow: '1 / span 2', fontSize: 28, lineHeight: '32px' }}>{count}</span>
            <span className="muted" style={{ gridColumn: 2, ...LABEL_SIZE }}>{text}</span>
        </button>
    );
}

/**
 * Data quality (masterplan skpi-redesign E13, package D1): two check cards on top - meetings without a company mapping and
 * persons without a target - and below them the one detail table of the selected check (§2 rule 5). The checks are pure
 * functions over the response (`strategicKpiDataQuality.ts`); the tab counter uses `countDataQualityChecks`. The tab
 * starts on the first check with a finding.
 */
export function DataQualityTab({ data, onSetTargets }: Props) {
    const mapping = data.dataQuality.meetingsWithoutCompanyMapping;
    const targets = personsWithoutTargets(data);
    const targetsCount = targetsFindingCount(targets);
    const personCount = targets.persons.length;
    const unknownCount = targets.busUnknown.length;
    const nothingListed = targetsCount === 0 && unknownCount === 0;
    const [chosen, setChosen] = useState<DataQualityCheck | null>(null);
    const check: DataQualityCheck = chosen
        ?? (checkHasFinding(data, 'mapping') ? 'mapping' : checkHasFinding(data, 'targets') ? 'targets' : 'mapping');
    const windowText = dataQualityWindowText(mapping.window.text);
    const { body, style } = useBodyTableOffset([check, data]);

    const mappingText = mapping.total > 0
        ? `${mapping.total} meetings without a company mapping (${windowText}) are missing from the visit counts — etl.DolderCompany.HubspotCode is not set for their company.`
        : `No meetings without a company mapping (${windowText}).`;
    const targetsText = targets.noPersons
        ? `No persons in ${data.year} yet - nothing to check.`
        : personCount > 0
            ? 'No target on any KPI with person targets: they show no light and do not count as behind.'
            : targetsCount > 0
                ? 'These BUs have no targets set; nobody there is listed.'
                : unknownCount > 0
                    ? NO_TARGETS_IN_SELECTION
                    : 'Every active person in a BU with targets has a target.';
    const personKpiText = targets.personKpiCount === 1 ? 'the only KPI with person targets' : `any of ${targets.personKpiCount} KPIs with person targets`;
    const buCount = targets.busWithoutTargets.length;

    return (
        <section className="space-y-4" data-testid="skpi-quality">
            <div className="grid gap-4 md:grid-cols-2">
                <CheckCard
                    check="mapping" title="Meetings without a company mapping" count={mapping.total} text={mappingText}
                    selected={check === 'mapping'} onSelect={setChosen} testId="dq-mapping"
                />
                <CheckCard
                    check="targets" title="Persons without a target" count={targetsCount} text={targetsText}
                    selected={check === 'targets'} onSelect={setChosen} testId="dq-targets"
                />
            </div>

            <div className="card flush">
                <div className="card-head" style={{ paddingBottom: 14 }}>
                    <h2>{check === 'mapping' ? 'Unmapped meetings by person' : 'Persons without a target'}</h2>
                    <div className="sub">
                        {check === 'mapping'
                            ? `${mapping.total} meetings, ${windowText}`
                            : `${personCount} ${personCount === 1 ? 'person' : 'persons'}${buCount > 0 ? ` · ${buCount} ${buCount === 1 ? 'BU' : 'BUs'} without targets` : ''}${unknownCount > 0 ? ' · selection: BU targets not visible' : ''} · ${data.year}`}
                    </div>
                </div>
                <div ref={body} style={style} data-testid="dq-detail">
                    {check === 'mapping' ? (
                        mapping.total === 0 ? (
                            <p className="muted" style={{ padding: '0 24px 20px', ...LABEL_SIZE }}>Nothing to fix: every meeting of the window is mapped to a company.</p>
                        ) : (
                            <div className="tbl-wrap">
                                <table className="tbl" data-testid="dq-mapping-table">
                                    <thead><tr><th scope="col">Person</th><th scope="col" className="n">Meetings</th></tr></thead>
                                    <tbody>
                                        {mapping.byPerson.map(p => (
                                            <tr key={p.staffId}>
                                                <td className="key">{p.fullName}</td>
                                                <td className="n num">{p.count}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot>
                                        <tr><td className="strong">{mapping.byPerson.length} {mapping.byPerson.length === 1 ? 'person' : 'persons'}</td><td className="n num strong">{mapping.total}</td></tr>
                                    </tfoot>
                                </table>
                            </div>
                        )
                    ) : targets.noPersons ? (
                        <p className="muted" style={{ padding: '0 24px 20px', ...LABEL_SIZE }} data-testid="dq-no-persons">No persons in {data.year} yet - there is nothing to check.</p>
                    ) : nothingListed ? (
                        <p className="muted" style={{ padding: '0 24px 20px', ...LABEL_SIZE }}>Nothing to fix: every active person in a BU with targets has one.</p>
                    ) : (
                        <div className="tbl-wrap">
                            <table className="tbl" data-testid="dq-targets-table">
                                <thead>
                                    <tr><th scope="col">Person</th><th scope="col">Company · team</th><th scope="col">BU</th><th scope="col">Finding</th><th scope="col"><span className="sr-only">Action</span></th></tr>
                                </thead>
                                <tbody>
                                    {targets.persons.map(p => (
                                        <tr key={p.staffId}>
                                            <td className="key">{p.fullName}</td>
                                            <td>{p.companyCode}{p.teamManagerName ? ` · ${p.teamManagerName}` : ''}</td>
                                            <td>{p.buCode}</td>
                                            <td>No target on {personKpiText}</td>
                                            <td className="n">
                                                <button type="button" className="btn quiet sm" onClick={() => onSetTargets({ staffId: p.staffId, buCode: p.buCode })}>
                                                    Set targets <ArrowRight className="i sm" aria-hidden="true" />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                    {targets.busWithoutTargets.map(bu => (
                                        <tr key={`bu-${bu}`} data-testid="dq-bu-no-targets">
                                            <td className="key">All persons</td>
                                            <td className="muted">–</td>
                                            <td>{bu}</td>
                                            <td>No targets set for this BU</td>
                                            <td className="n">
                                                <button type="button" className="btn quiet sm" onClick={() => onSetTargets({ buCode: bu })}>
                                                    Set targets <ArrowRight className="i sm" aria-hidden="true" />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                    {targets.busUnknown.map(bu => (
                                        <tr key={`unknown-${bu}`} data-testid="dq-bu-unknown">
                                            <td className="key">Persons in the selection</td>
                                            <td className="muted">–</td>
                                            <td>{bu}</td>
                                            <td>{NO_TARGETS_IN_SELECTION}</td>
                                            <td className="n"><span className="sr-only">No action</span></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
}
